import { FC, useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Image,
  Divider,
  Icon,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useToast,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper
} from '@chakra-ui/react';
import {
  MdArrowBack,
  MdEdit,
  MdAdd,
  MdVideoLibrary,
  MdLibraryBooks,
  MdQuiz,
} from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES, base64 } from '@bemap/utils';
import { Panel, Card, PageHeading } from '@bemap/ui';
import { getCourseById, createModule, updateModule, getCourseStudents, Course, Module, Content } from '@/services/university-service';
import CourseStudentsList from '@/components/university/CourseStudentsList';


const ViewCoursePage: FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const toast = useToast();
  const { t } = useTranslation();

  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showEditModuleModal, setShowEditModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentCount, setStudentCount] = useState(0);

  const cardBg = useColorModeValue('white', 'gray.800');

  // Load course data from API
  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) return;

      try {
        setLoading(true);

        // Load course and student count concurrently
        const [courseResponse, studentsResponse] = await Promise.all([
          getCourseById(parseInt(courseId)),
          getCourseStudents(parseInt(courseId))
        ]);

        if (courseResponse.statusCode === 200 && courseResponse.message) {
          setCourse(courseResponse.message as Course);
        } else {
          throw new Error(courseResponse.message || t('university.courses.course_not_found'));
        }

        if (studentsResponse.statusCode === 200 && studentsResponse.message) {
          const students = studentsResponse.message as any[];
          setStudentCount(students.length);
        }
      } catch (error: any) {
        console.error('Error loading course:', error);
        toast({
          title: t('university.courses.error_loading_courses'),
          description: error.message || t('general.error'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [courseId, navigate, toast, t]);

  const handleBack = () => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES);
  };

  const handleEditCourse = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}/edit`);
  };

  const handleAddModule = () => {
    setShowModuleModal(true);
  };

  const handleAddContent = (moduleId: string) => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_SELECT.replace(':courseId', courseId!).replace(':moduleId', moduleId));
  };

  const handleEditModule = (module: Module) => {
    setEditingModule(module);
    setShowEditModuleModal(true);
  };

  const handleViewContent = (content: Content, moduleId: string) => {
    navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_VIEW
      .replace(':courseId', courseId!)
      .replace(':moduleId', moduleId)
      .replace(':contentId', content.id.toString()));
  };

  const handleCreateModule = async (moduleData: { title: string; description: string; order: number }) => {
    if (!course) return;

    setIsSubmitting(true);
    try {
      const payload: Partial<Module> = {
        title: moduleData.title,
        description: moduleData.description,
        order: moduleData.order,
        courseId: course.id,
        active: true,
        completed: false,
        progress: 0,
        durationMinutes: 0
      };

      const response = await createModule(payload);

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast({
          title: t('university.courses.modules.module_created'),
          description: `${t('university.courses.modules.edit_module')}"${moduleData.title}" ${t('university.courses.modules.module_created')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Reload course data to get the new module
        const courseResponse = await getCourseById(course.id);
        if (courseResponse.statusCode === 200 && courseResponse.message) {
          setCourse(courseResponse.message as Course);
        }

        setShowModuleModal(false);
      } else {
        throw new Error(response.message || t('university.courses.modules.error_creating_module'));
      }
    } catch (error: any) {
      console.error('Error creating module:', error);
      toast({
        title: t('university.courses.modules.error_creating_module'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateModule = async (moduleData: { title: string; description: string; order: number }) => {
    if (!editingModule) return;

    setIsSubmitting(true);
    try {
      const payload: Partial<Module> = {
        title: moduleData.title,
        description: moduleData.description,
        order: moduleData.order
      };

      const response = await updateModule(editingModule.id, payload);

      if (response.statusCode === 200) {
        toast({
          title: t('university.courses.modules.module_updated'),
          description: `${t('university.courses.modules.edit_module')}"${moduleData.title}" ${t('university.courses.modules.module_updated')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Reload course data to get the updated module
        const courseResponse = await getCourseById(course!.id);
        if (courseResponse.statusCode === 200 && courseResponse.message) {
          setCourse(courseResponse.message as Course);
        }

        setShowEditModuleModal(false);
        setEditingModule(null);
      } else {
        throw new Error(response.message || t('university.courses.modules.error_updating_module'));
      }
    } catch (error: any) {
      console.error('Error updating module:', error);
      toast({
        title: t('university.courses.modules.error_updating_module'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'green';
      case 'intermediate': return 'orange';
      case 'advanced': return 'red';
      default: return 'gray';
    }
  };

  const getContentIcon = (type: string) => {
    switch (type) {
      case 'video': return MdVideoLibrary;
      case 'document': return MdLibraryBooks;
      case 'quiz': return MdQuiz;
      default: return MdLibraryBooks;
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  // Calculate statistics
  const totalContents = course?.modules?.reduce((sum: number, module: Module) =>
    sum + (module.contents?.length || 0), 0) || 0;
  const completedContents = course?.modules?.reduce((sum: number, module: Module) =>
    sum + (module.contents?.filter((content: Content) => content.completed).length || 0), 0) || 0;
  const progressPercentage = totalContents > 0 ? Math.round((completedContents / totalContents) * 100) : 0;

  if (loading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>{t('general.loading')}</Text>
        </VStack>
      </Center>
    );
  }

  if (!course) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Text>{t('university.courses.course_not_found')}</Text>
          <Button onClick={handleBack}>{t('university.courses.back_to_courses')}</Button>
        </VStack>
      </Center>
    );
  }

  return (
    <>
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <PageHeading title={course.title} actions={[
          {
            label: t('university.courses.back_to_courses'),
            onClick: handleBack,
            icon: <Icon as={MdArrowBack} />,
            variant: 'outline'
          },
          {
            label: t('university.courses.edit_course'),
            onClick: handleEditCourse,
            icon: <Icon as={MdEdit} />,
            variant: 'outline'
          }
        ]} />

        {/* Course Overview */}
        <Panel heading={t('university.courses.course_overview')} padding={6}>
          <VStack align="stretch" spacing={6}>
            <HStack spacing={6} align="start">
              {course.thumbnailUrl && (
                <Image
                  src={base64(course.thumbnailUrl)}
                  alt={course.title}
                  borderRadius="lg"
                  maxW="300px"
                  objectFit="cover"
                />
              )}

              <VStack align="start" spacing={4} flex={1}>
                <VStack align="start" spacing={2}>
                  <HStack spacing={3}>
                    <Badge colorScheme={getLevelColor(course.level)} size="lg">
                      {course.level}
                    </Badge>
                    <Badge colorScheme={course.active ? 'green' : 'gray'} size="lg">
                      {course.active ? t('university.courses.status.active') : t('university.courses.status.draft')}
                    </Badge>
                  </HStack>

                  <Text fontSize="lg" color="gray.600">
                    {course.description}
                  </Text>
                </VStack>

                <VStack align="start" spacing={2}>
                  <Text fontSize="sm" color="gray.500">
                    <strong>{t('university.courses.instructor')}:</strong> {course.instructor}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    <strong>{t('university.courses.category')}:</strong> {course.category}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    <strong>{t('university.courses.duration_hours')}:</strong> {formatDuration(course.durationMinutes)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    <strong>{t('university.courses.price')}:</strong> ${course.price || 0}
                  </Text>
                </VStack>
              </VStack>
            </HStack>

            <Divider />

            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
              <Stat>
                <StatLabel>{t('university.courses.modules.enrolled_students')}</StatLabel>
                <StatNumber color="blue.600">{studentCount}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  {studentCount} {t('university.courses.modules.enrolled_students')}
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>{t('university.courses.modules.content_items')}</StatLabel>
                <StatNumber color="green.600">{course.modules?.length || 0}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  {course.modules?.length || 0} {t('university.courses.modules.total_modules')}
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>{t('university.courses.modules.content_items')}</StatLabel>
                <StatNumber color="purple.600">{totalContents}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  {completedContents} {t('university.courses.modules.completed')}
                </StatHelpText>
              </Stat>

              <Stat>
                <StatLabel>{t('university.courses.modules.completion_rate')}</StatLabel>
                <StatNumber color="orange.600">{progressPercentage}%</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  {completedContents} {t('university.courses.modules.completed')} {totalContents}
                </StatHelpText>
              </Stat>
            </SimpleGrid>
          </VStack>
        </Panel>

        {/* Course Content */}
        <Panel heading={t('university.courses.modules.course_content')} padding={6}>
          <Tabs colorScheme="blue">
            <TabList>
              <Tab>{t('university.courses.modules.course_content')}</Tab>
              <Tab>{t('university.courses.modules.students')}</Tab>
              {/*<Tab>{t('university.courses.modules.analytics')}</Tab>*/}
              <Tab>{t('university.courses.modules.settings')}</Tab>
            </TabList>

            <TabPanels>
              {/* Course Content Tab */}
              <TabPanel p={0}>
                <VStack align="stretch" spacing={4}>
                  {course.modules?.map((module: Module) => (
                    <Card key={module.id} bg={cardBg}>
                      <VStack align="stretch" spacing={4}>
                        <HStack justify="space-between" align="center">
                          <Box>
                            <Heading size="md">{module.title}</Heading>
                            <Text color="gray.600" fontSize="sm">{module.description}</Text>
                          </Box>
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              colorScheme="blue"
                              leftIcon={<Icon as={MdEdit} />}
                              onClick={() => handleEditModule(module)}
                            >
                              {t('university.courses.modules.edit_module')}
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="green"
                              leftIcon={<Icon as={MdAdd} />}
                              onClick={() => handleAddContent(module.id.toString())}
                            >
                              {t('university.courses.modules.add_content')}
                            </Button>
                          </HStack>
                        </HStack>

                        <VStack align="stretch" spacing={2}>
                          {module.contents?.map((content: Content) => (
                            <HStack
                              key={content.id}
                              p={3}
                              bg={content.completed ? 'green.50' : 'gray.50'}
                              borderRadius="md"
                              cursor="pointer"
                              _hover={{ bg: content.completed ? 'green.100' : 'gray.100' }}
                              onClick={() => handleViewContent(content, module.id.toString())}
                            >
                              <Icon as={getContentIcon(content.type)} color="blue.500" />
                              <Text flex={1} fontSize="sm">
                                {content.title}
                              </Text>
                              <HStack spacing={2}>
                                {content.duration && (
                                  <Text fontSize="xs" color="gray.500">
                                    {content.duration}
                                  </Text>
                                )}
                                {content.completed ? (
                                  <Badge colorScheme="green" size="sm">✓</Badge>
                                ) : (
                                  <Badge colorScheme="gray" size="sm">{t('university.courses.modules.pending')}</Badge>
                                )}
                              </HStack>
                            </HStack>
                          ))}
                        </VStack>
                      </VStack>
                    </Card>
                  ))}

                  {(!course.modules || course.modules.length === 0) && (
                    <Box textAlign="center" py={8}>
                      <Text color="gray.500" fontSize="lg" mb={4}>
                        {t('university.courses.modules.no_modules_available')}
                      </Text>
                    </Box>
                  )}

                  <Button
                    variant="outline"
                    colorScheme="blue"
                    leftIcon={<Icon as={MdAdd} />}
                    onClick={handleAddModule}
                    size="lg"
                    h="60px"
                  >
                    {t('university.courses.modules.add_new_module')}
                  </Button>
                </VStack>
              </TabPanel>

              {/* Students Tab */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  {courseId && <CourseStudentsList courseId={parseInt(courseId)} />}
                </VStack>
              </TabPanel>

              {/* Analytics Tab 
              <TabPanel>
                <Card bg={cardBg}>
                  <Text>{t('university.courses.modules.course_analytics')}</Text>
                </Card>
              </TabPanel>*/}

              {/* Settings Tab */}
              <TabPanel>
                <Card bg={cardBg}>
                  <Text>{t('university.courses.modules.advanced_course_settings')}</Text>
                </Card>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Panel>
      </VStack>



      {/* Create Module Modal */}
      <Modal isOpen={showModuleModal} onClose={() => setShowModuleModal(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('university.courses.modules.create_new_module')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateModuleForm onSubmit={handleCreateModule} isSubmitting={isSubmitting} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Edit Module Modal */}
      <Modal isOpen={showEditModuleModal} onClose={() => setShowEditModuleModal(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('university.courses.modules.edit_module')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateModuleForm
              onSubmit={handleUpdateModule}
              isSubmitting={isSubmitting}
              initialData={editingModule ? {
                title: editingModule.title,
                description: editingModule.description,
                order: editingModule.order
              } : undefined}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

// Create Module Form Component
interface CreateModuleFormProps {
  onSubmit: (data: { title: string; description: string; order: number }) => void;
  isSubmitting: boolean;
  initialData?: { title: string; description: string; order: number };
}

const CreateModuleForm: FC<CreateModuleFormProps> = ({ onSubmit, isSubmitting, initialData }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [order, setOrder] = useState(initialData?.order || 1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim() && description.trim()) {
      onSubmit({ title: title.trim(), description: description.trim(), order });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4}>
        <FormControl isRequired>
          <FormLabel>{t('university.courses.modules.module_title')}</FormLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('university.placeholders.course_title')}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>{t('university.courses.modules.module_description')}</FormLabel>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('university.placeholders.course_description')}
            rows={3}
          />
        </FormControl>

        <FormControl>
          <FormLabel>{t('university.courses.modules.order')}</FormLabel>
          <NumberInput min={1} value={order} onChange={(_, value) => setOrder(value)}>
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>

        <HStack justify="flex-end" w="full" pt={4}>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={initialData ? t('general.updating') : t('general.creating')}
            isDisabled={!title.trim() || !description.trim()}
          >
            {initialData ? t('university.courses.modules.edit_module') : t('university.courses.modules.create_new_module')}
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};



export default ViewCoursePage; 